import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { categoryAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

const slugify = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const emptyForm = { name: '', description: '' };

const Campsites = () => {
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', search: '' });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const search = query.search || '';

  const { data, isLoading } = useQuery(
    ['categories', 'trek', page, limit, search],
    () => categoryAPI.list({ type: 'trek', page, limit, search: search || undefined })
  );
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: items.length };

  const createMutation = useMutation((payload) => categoryAPI.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const updateMutation = useMutation(({ id, payload }) => categoryAPI.update(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setEditing(null);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const deleteMutation = useMutation((id) => categoryAPI.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(['categories'])
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (editing) {
      updateMutation.mutate({
        id: editing,
        payload: { name: form.name.trim(), description: form.description?.trim() || '' }
      });
    } else {
      const slug = slugify(form.name) || `campsite-${Date.now()}`;
      createMutation.mutate({
        name: form.name.trim(),
        description: form.description?.trim() || '',
        slug,
        type: 'trek',
        isActive: true,
        order: 0
      });
    }
  };

  const onEdit = (item) => {
    setEditing(item.id);
    setShowForm(true);
    setForm({
      name: item.name || '',
      description: item.description || ''
    });
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Campsites"
        subtitle="Manage campsites. Each trek can be linked to one campsite."
        actions={
          !showForm ? (
            <button
              className="module-btn"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setShowForm(true);
              }}
            >
              Add Campsite
            </button>
          ) : null
        }
      />

      {showForm ? (
        <form className="module-card module-form-grid" onSubmit={onSubmit} style={{ maxWidth: 560 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required placeholder="e.g. Polo Forest Trekking Camp" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea rows="3" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Short description of the campsite..." />
          </div>
          <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem', gridColumn: '1 / -1' }}>
            <button className="module-btn" type="submit" disabled={createMutation.isLoading || updateMutation.isLoading}>
              {editing ? 'Update Campsite' : 'Create Campsite'}
            </button>
            <button
              type="button"
              className="module-btn secondary"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setShowForm(false);
              }}
            >
              Cancel
            </button>
            {editing && (
              <button type="button" className="module-btn secondary" onClick={() => { setEditing(null); setForm(emptyForm); }}>
                Reset
              </button>
            )}
          </div>
        </form>
      ) : null}

      {!showForm ? (
        <FilterBar>
          <input
            type="text"
            className="module-filter-input"
            placeholder="Search campsites..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
          />
        </FilterBar>
      ) : null}

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading campsites...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No campsites yet"
            description="Add a campsite to assign to treks."
            actionLabel={!showForm ? 'Add Campsite' : undefined}
            onAction={
              !showForm
                ? () => {
                    setEditing(null);
                    setForm(emptyForm);
                    setShowForm(true);
                  }
                : undefined
            }
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td style={{ maxWidth: 360 }}>{item.description || '–'}</td>
                  <td className="module-actions">
                    <button className="module-link-btn" onClick={() => onEdit(item)}>Edit</button>
                    <button
                      className="module-link-btn danger"
                      onClick={() => {
                        if (window.confirm('Delete this campsite?')) deleteMutation.mutate(item.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="module-list-pagination">
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <div className="module-list-pagination-btns">
                <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                <button type="button" className="module-btn secondary" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Campsites;
